import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useCallback, useEffect } from 'react';
import { X, CaretLeft, CaretRight } from '@phosphor-icons/react';
import useEmblaCarousel from 'embla-carousel-react';

const projects = [
  {
    id: 1,
    title: 'Chittaway Bay Tavern',
    category: 'Commercial',
    description: 'Complete transformation of indoor and outdoor bars and dining areas. Brett coordinated all trades across a live venue, delivering new bar builds, dining fit-outs, an outdoor beer garden, and a children\'s playground.',
    images: [
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/7pd5e9re_chittaway07.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/70c0zta7_chittaway08.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/1m8x6vtg_chittaway09.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/8ms1nkkg_chittaway01.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/0rnorpmk_chittaway02.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/krt9l7ok_chittaway03.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/l8phr7vu_chittaway04.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/bhsnurr7_chittaway05.jpg',
    ],
    thumbnail: 'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/7pd5e9re_chittaway07.jpg',
  },
  {
    id: 2,
    title: 'Residential New Build, The Entrance North',
    category: 'Residential',
    description: 'DA-approved new build, Central Coast Council DA/1280/2021. Full construction from slab to handover. Custom kitchen, stone fireplace feature wall, and quality finishes throughout.',
    images: [
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/973c47l5_Rimsie_website4.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/0sq2pq2h_Rimsie_website5.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/2fkiqkh1_Rimsie_website6.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/jaxh12a3_Rimsie_website.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/8ncv11bf_Rimsie_website2.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/vi8q2l7g_Rimsie_website3.jpg',
    ],
    thumbnail: 'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/973c47l5_Rimsie_website4.jpg',
  },
  {
    id: 3,
    title: 'Gaming Room Renovations',
    category: 'Gaming Rooms',
    description: 'Specialised gaming room renovations across five licensed venues: Tea Gardens Hotel, The Settlers Hotel, Chittaway Bay Tavern, Swansea Hotel, and North Lakes Tavern. Compliance-driven fitouts delivered on schedule with minimal disruption to trading.',
    images: [
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/bzdo94we_gaming_website06.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/3ih00qc0_gaming_website01.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/k1e6zqcf_gaming_website02.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/xme4t02u_gaming_website03.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/fq6tk7pt_gaming_website05.jpg',
    ],
    thumbnail: 'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/xme4t02u_gaming_website03.jpg',
  },
  {
    id: 4,
    title: 'Roof & Repairs',
    category: 'Commercial',
    description: 'From site setup to final fix — Rimsie Constructions Pty Ltd handles repairs, refurbishments, and rectification works across commercial and residential properties on the Central Coast.',
    images: [
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/xvjmr2jf_roof_website01.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/qebh8fej_roof_website02.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/o1qis91i_roof_website03.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/coi2e0vt_roof_website04.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/2pm70um2_roof_website05.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/hnwgxnzj_roof_website06.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/vis0ja2a_window_website01.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/h6gt42lf_window_website02.jpg',
    ],
    thumbnail: 'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/xvjmr2jf_roof_website01.jpg',
  },
];

const categories = ['All', 'Commercial', 'Residential', 'Gaming Rooms'];

/* ── Carousel Modal ─────────────────────────────────── */
const CarouselModal = ({ project, onClose }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [currentSlide, setCurrentSlide] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCurrentSlide(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/95 z-50 flex flex-col"
      onClick={onClose}
      data-testid="carousel-modal"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <p className="overline mb-1">{project.category}</p>
          <h3 className="text-heading text-lg md:text-xl font-heading">{project.title}</h3>
        </div>
        <button
          onClick={onClose}
          data-testid="carousel-close"
          className="w-10 h-10 flex items-center justify-center text-heading/70 hover:text-heading transition-colors"
          aria-label="Close gallery"
        >
          <X size={24} weight="light" />
        </button>
      </div>

      {/* Carousel */}
      <div
        className="flex-1 flex items-center justify-center relative px-4 md:px-16"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Prev button */}
        <button
          onClick={scrollPrev}
          data-testid="carousel-prev"
          className="absolute left-2 md:left-6 z-10 w-12 h-12 flex items-center justify-center bg-primary-bg/60 hover:bg-primary-bg/90 text-heading transition-colors"
          aria-label="Previous photo"
        >
          <CaretLeft size={28} weight="light" />
        </button>

        {/* Slides */}
        <div className="overflow-hidden w-full max-w-5xl mx-auto" ref={emblaRef}>
          <div className="flex">
            {project.images.map((img, idx) => (
              <div
                key={`slide-${img.slice(-20)}`}
                className="flex-[0_0_100%] min-w-0 flex items-center justify-center px-2"
              >
                <img
                  src={img}
                  alt={`${project.title} - Photo ${idx + 1}`}
                  className="max-h-[70vh] w-auto max-w-full object-contain"
                  data-testid={`carousel-image-${idx}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Next button */}
        <button
          onClick={scrollNext}
          data-testid="carousel-next"
          className="absolute right-2 md:right-6 z-10 w-12 h-12 flex items-center justify-center bg-primary-bg/60 hover:bg-primary-bg/90 text-heading transition-colors"
          aria-label="Next photo"
        >
          <CaretRight size={28} weight="light" />
        </button>
      </div>

      {/* Dots + Description */}
      <div
        className="px-6 py-5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Dots */}
        <div className="flex justify-center gap-2 mb-4">
          {project.images.map((_, idx) => (
            <button
              key={`dot-${idx}`}
              onClick={() => emblaApi && emblaApi.scrollTo(idx)}
              data-testid={`carousel-dot-${idx}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                idx === currentSlide
                  ? 'bg-accent w-8'
                  : 'bg-heading/20 w-4 hover:bg-heading/40'
              }`}
              aria-label={`Go to photo ${idx + 1}`}
            />
          ))}
        </div>

        {/* Description */}
        <p className="text-body-text text-sm text-center max-w-2xl mx-auto leading-relaxed">
          {project.description}
        </p>

        <p className="text-body-muted text-xs text-center mt-2">
          {currentSlide + 1} / {project.images.length}
        </p>
      </div>
    </motion.div>
  );
};

/* ── Gallery Section ────────────────────────────────── */
const Gallery = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);

  const filteredProjects =
    activeFilter === 'All'
      ? projects
      : projects.filter((p) => p.category === activeFilter);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="section-padding bg-secondary-bg"
      ref={ref}
    >
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="overline mb-4">Portfolio</p>
          <h2
            data-testid="gallery-heading"
            className="text-4xl sm:text-5xl font-heading font-light text-heading"
          >
            Our Work
          </h2>
        </motion.div>

        {/* Filter buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveFilter(category)}
              data-testid={`filter-${category.toLowerCase().replace(' ', '-')}`}
              className={`px-6 py-2 text-sm font-medium transition-all duration-300 ${
                activeFilter === category
                  ? 'bg-accent text-primary-bg'
                  : 'bg-primary-bg border border-border-subtle text-heading hover:border-accent/50'
              }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Projects grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="masonry-grid"
        >
          {filteredProjects.map((project) => (
            <motion.div
              key={project.id}
              variants={itemVariants}
              layout
              transition={{ duration: 0.5 }}
              data-testid={`project-card-${project.id}`}
              className="group relative overflow-hidden cursor-pointer"
              onClick={() => setSelectedProject(project)}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={project.thumbnail}
                  alt={project.title}
                  className="w-full h-full object-cover image-zoom"
                />
              </div>

              {/* Overlay - always visible */}
              <div className="absolute inset-0 bg-gradient-to-t from-primary-bg via-primary-bg/40 to-transparent flex flex-col justify-end p-6">
                <p className="overline mb-2">{project.category}</p>
                <h3 className="text-heading text-lg font-heading mb-2">
                  {project.title}
                </h3>
                <div className="flex items-center gap-2 text-accent text-sm">
                  <span>View Photos ({project.images.length})</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Carousel Modal */}
      {selectedProject && (
        <CarouselModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </section>
  );
};

export default Gallery;
